import { Link } from 'react-router-dom';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

import { getAvatarColorClass } from '@/lib/avatarColor';

import type { UpcomingCall } from '@/types/dashboard.type';

type Props = {
  calls: UpcomingCall[];
  loading: boolean;
};

function getInitials(name: string): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const UpcomingCallSkeleton = () => (
  <div className="flex items-center justify-between rounded-md border p-3">
    <div className="flex items-center gap-3">
      <Skeleton className="h-8 w-8 rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
    <Skeleton className="h-8 w-16" />
  </div>
);

function formatRecipientNames(names: string[]): string {
  if (!names.length) return 'No recipients';
  if (names.length === 1) return names[0];
  return `${names[0]} and ${names.length - 1} more`;
}

const UpcomingCallRow = ({ call }: { call: UpcomingCall }) => {
  const displayName = formatRecipientNames(call.recipientNames);
  const initials = getInitials(call.recipientNames[0] ?? '');

  return (
    <div className="flex items-center justify-between rounded-md border p-3">
      <div className="flex items-center gap-3">
        <Avatar className="h-9 w-9">
          <AvatarFallback className={getAvatarColorClass(call.id || displayName)}>
            {initials}
          </AvatarFallback>
        </Avatar>
        <div>
          <div className="font-medium">{displayName}</div>
          <div className="text-xs text-muted-foreground">
            {call.when} · {call.agentName ?? 'No agent'} · {call.totalRecipients} recipient
            {call.totalRecipients !== 1 ? 's' : ''}
          </div>
        </div>
      </div>
      <Button asChild variant="outline" size="sm">
        <Link to={`/batch-calls?call=${call.id}`}>View</Link>
      </Button>
    </div>
  );
};

const EmptyState = () => (
  <div className="text-center py-4 text-muted-foreground">
    <p className="text-sm">No upcoming calls scheduled</p>
    <Button asChild variant="outline" size="sm" className="mt-2">
      <Link to="/batch-calls">Schedule a call</Link>
    </Button>
  </div>
);

export const UpcomingCalls = ({ calls, loading }: Props) => {
  return (
    <Card className="bg-muted rounded-2xl p-1 pt-0 gap-0 border-0 shadow-none flex flex-col">
      <CardHeader className="flex flex-col px-5 py-3">
        <CardTitle className="text-sm">Upcoming calls</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 bg-card p-5 rounded-xl shadow-sm flex-1">
        {loading ? (
          ['skeleton-1', 'skeleton-2', 'skeleton-3'].map(id => <UpcomingCallSkeleton key={id} />)
        ) : calls.length > 0 ? (
          calls.map((call, i) => <UpcomingCallRow key={call.id ?? i} call={call} />)
        ) : (
          <EmptyState />
        )}
      </CardContent>
    </Card>
  );
};
